import React from 'react';
import { useTranslation } from 'react-i18next';
import { Identity } from 'models';
import { useForm } from '@weezevent/weezjs-core';

import { Button, DropdownLight, Message, Input, InputLabel, Modal, ToastContext } from '@weezevent/nacre';
import css from './identities.module.css';

const FIRST_OPTION_INDEX = 1;

const IdentityModal = ({ id = null, organizationId, fromAddress, replyTo, syncIdentities, open, onClose, domains, isIdentityAdmin, isIdentityManager }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [displayWarning, setDisplayWarning] = React.useState(false);

    const action = id ? 'edit' : 'create';

    const [senderEmailLocal, senderEmailDomain] = fromAddress?.email.split('@') ?? ['', ''];

    const availableDomains = domains?.filter(d => d.verification_status === 'success' || d.verification_status === 'pending') || [];

    const customDomains = availableDomains.filter(d => d.name !== 'weezevent.com');

    const isEditForbidden = React.useMemo(() => {
        return !isIdentityAdmin && (senderEmailDomain === 'weezevent.com' || customDomains.length === 0);
    }, [isIdentityAdmin, senderEmailDomain, customDomains.length]);

    const capacityDomains = React.useMemo(() => {
        if (isIdentityAdmin || isEditForbidden) return availableDomains;

        if (isIdentityManager && customDomains.length > 0) return customDomains;

        return [];
    }, [isIdentityAdmin, isEditForbidden, isIdentityManager, availableDomains, customDomains]);

    const domainsOptions = React.useMemo(() => {
        if (capacityDomains.length === 0) return [{ label: '@weezevent.com', value: FIRST_OPTION_INDEX }];
        return capacityDomains.map((d, i) => d.toOptions(i));
    }, [capacityDomains]);

    const [form, setForm] = useForm({
        sender_name: fromAddress?.name ?? '',
        sender_email_local: senderEmailLocal || (isEditForbidden ? 'support' : ''),
        domain_value: domainsOptions.find(opt => opt.label.includes(senderEmailDomain))?.value || FIRST_OPTION_INDEX,
        reply_name: replyTo?.name ?? 'Support',
        reply_email: replyTo?.email ?? 'support@weezevent.com',
        organization: null,
        media: 'email'
    });
    const currentDomain = domainsOptions[form.domain_value - 1];
    const fullSenderEmail = `${form.sender_email_local}${currentDomain.label}`;

    const handleSelect = value => {
        setForm('domain_value', value);
        const selected = domainsOptions[value - 1];
        setDisplayWarning(selected.status === 'pending');
    };

    const handleSubmit = React.useCallback(() => {
        const payload = {
            from_address: {
                name: form.sender_name,
                email: fullSenderEmail
            },
            reply_to: {
                name: form.reply_name || 'Support',
                email: form.reply_email || 'support@weezevent.com'
            },
            organization: organizationId,
            media: 'email'
        };

        const newIdentity = new Identity({ ...payload, id });

        return newIdentity
            .save({ organizationId })
            .then(response => {
                if ([201, 200].includes(response.status)) {
                    toast.success(t('sauron.toasts.identities.create-identity-success'));
                    syncIdentities();
                }
                onClose();
            })
            .catch(() => toast.error(t('sauron.toasts.error')));
    }, [form, organizationId, fullSenderEmail]);

    return (
        <Modal
            size="large"
            open={open}
            allowExternalClosing={true}
            onClose={onClose}
            title={t(`sauron.emails.identities.${action}.title`)}
            subtitle={t(`sauron.emails.identities.${action}.subtitle`)}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    <Button primary inverted onClick={handleSubmit} disabled={!form.sender_name || !form.sender_email_local} label={t('common.cta.save')} />
                </Button.Group>
            }
        >
            <div className={css['idendity-input-container']}>
                <div className={css['identity-input']}>
                    {availableDomains.length === 0 ? (
                        <Message type="danger" title={t('sauron.emails.identities.create.message-no-domain.title')}>
                            {t('sauron.emails.identities.create.message-no-domain.content')}
                        </Message>
                    ) : (
                        <div className={css['input-wrapper']}>
                            <InputLabel label={t('sauron.emails.identities.create.sender.address')} />
                            <div className={css['identity-input-email']}>
                                <span>
                                    <Input
                                        className={css['identity-input-email-locale']}
                                        placeholder={t('sauron.emails.identities.create.sender.placeholder.address')}
                                        value={form.sender_email_local}
                                        disabled={isEditForbidden}
                                        onChange={({ target: { value } }) => setForm('sender_email_local', value)}
                                    />
                                    <DropdownLight
                                        className={css['identity-input-domain']}
                                        onChange={value => handleSelect(value)}
                                        value={form.domain_value}
                                        items={domainsOptions}
                                        disabled={isEditForbidden}
                                    />
                                </span>
                            </div>
                        </div>
                    )}
                    {displayWarning && (
                        <Message type="warning" title={t('sauron.emails.identities.create.message-pending-domain.title')}>
                            {t('sauron.emails.identities.create.message-pending-domain.content')}
                        </Message>
                    )}
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.sender.name')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.sender.placeholder.name')}
                            value={form.sender_name}
                            onChange={({ target: { value } }) => setForm('sender_name', value)}
                        />
                    </div>
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.reply-to.name')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.reply-to.placeholder.name')}
                            value={form.reply_name}
                            onChange={({ target: { value } }) => setForm('reply_name', value)}
                        />
                    </div>
                </div>

                <div className={css['identity-input']}>
                    <div className={css['input-wrapper']}>
                        <InputLabel label={t('sauron.emails.identities.create.reply-to.address')} />
                        <Input
                            placeholder={t('sauron.emails.identities.create.reply-to.placeholder.address')}
                            value={form.reply_email}
                            onChange={({ target: { value } }) => setForm('reply_email', value)}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default IdentityModal;
